import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { DxcStateIconName, DxcTone } from '../dxc.types';

/**
 * Iconografía de estado. Dibujados uno por uno para las situaciones reales del
 * producto, no adaptados de un set genérico: un icono de caja vacía sirve igual
 * para un carrito sin productos que para una flota sin unidades, y por eso no
 * dice nada.
 *
 * La animación explica la causa: el contorno punteado que avanza dice «existe
 * pero no está dado de alta», los anillos que se apagan dicen «la señal se
 * perdió», las dos fichas que rebotan dicen «no coinciden».
 *
 * Con `prefers-reduced-motion` se congelan todos y siguen siendo legibles — un
 * icono cuyo significado DEPENDA del movimiento está mal dibujado, y ese es el
 * criterio que usamos para aceptar o descartar uno nuevo.
 */
@Component({
    standalone: true,
    selector: 'dxc-state-icon',
    templateUrl: './dxc-state-icon.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcStateIconComponent {

    @Input() name: DxcStateIconName = 'sin-cargas';
    /** Se omite para tomar el tono propio del icono. */
    @Input() tone?: DxcTone;
    /** 96 en estado vacío, 44 dentro de una alerta. */
    @Input() size = 96;
    /** Vacío cuando el texto contiguo ya lo describe. */
    @Input() ariaLabel = '';

    /** Tono natural de cada estado; `tone` lo sobreescribe. */
    static readonly DEFAULT_TONE: Record<string, DxcTone> = { 'sin-gps': 'danger', 'sin-timbrar': 'warning', 'timbrado-ok': 'success', 'pac-rechazo': 'danger', 'sin-acceso': 'danger', 'sin-duplicados': 'success' };

    get hostClasses() {
        const tone = this.tone ?? DxcStateIconComponent.DEFAULT_TONE[this.name] ?? 'default';
        const classes = ['dxc-state-icon'];
        if (tone !== 'default') classes.push(`dxc-state-icon--${tone}`);
        if (this.size <= 56) classes.push('dxc-state-icon--sm');
        return classes.join(' ');
    }
}
